// fallow-ignore-next-line unused-export
export const CARD_PREVIEW_WS_WAIT_TIMEOUT_MS = 30_000;

// fallow-ignore-next-line unused-export
export const CARD_PREVIEW_WS_WAIT_TIMEOUT_MESSAGE =
	'Preview data is taking longer than expected. Check the report connection or try again.';

// fallow-ignore-next-line unused-export
export function clearPreviewLoaderWsWaitTimeout(
	wsWaitTimeoutId: ReturnType<typeof setTimeout> | undefined
): undefined {
	if (wsWaitTimeoutId !== undefined) {
		clearTimeout(wsWaitTimeoutId);
	}
	return undefined;
}

export interface PreviewWsWaitState {
	wsWaitCardId: string | undefined;
	wsWaitRequestToken: number | undefined;
	wsWaitBaselineFingerprint: string;
	/** When set (manual refresh), complete on `executionId` change instead of payload fingerprint. */
	wsWaitBaselineExecutionId?: string;
	wsWaitTimeoutId: ReturnType<typeof setTimeout> | undefined;
}

export function createPreviewWsWaitState(): PreviewWsWaitState {
	return {
		wsWaitCardId: undefined,
		wsWaitRequestToken: undefined,
		wsWaitBaselineFingerprint: '',
		wsWaitBaselineExecutionId: undefined,
		wsWaitTimeoutId: undefined,
	};
}

export function clearPreviewWsWaitState(
	state: PreviewWsWaitState,
	bumpRevision: () => void
): void {
	const hadWait =
		state.wsWaitCardId !== undefined ||
		state.wsWaitRequestToken !== undefined;
	state.wsWaitCardId = undefined;
	state.wsWaitRequestToken = undefined;
	state.wsWaitBaselineFingerprint = '';
	state.wsWaitBaselineExecutionId = undefined;
	state.wsWaitTimeoutId = clearPreviewLoaderWsWaitTimeout(
		state.wsWaitTimeoutId
	);
	if (hadWait) {
		bumpRevision();
	}
}

export function beginPreviewDataLoaderWsWait(args: {
	state: PreviewWsWaitState;
	cardId: string;
	requestToken: number;
	waitBaselineFingerprint: string;
	hasUpdatedRenderableData: () => boolean;
	finalizeRequest: (requestToken: number) => void;
	isActiveRequest: (requestToken: number) => boolean;
	bumpRevision: () => void;
	callbacks?: { onWsWaitTimeout?: (message: string) => void };
}): void {
	if (args.hasUpdatedRenderableData()) {
		args.finalizeRequest(args.requestToken);
		return;
	}
	args.state.wsWaitBaselineFingerprint = args.waitBaselineFingerprint;
	args.state.wsWaitRequestToken = args.requestToken;
	args.state.wsWaitCardId = args.cardId;
	args.bumpRevision();
	args.state.wsWaitTimeoutId = clearPreviewLoaderWsWaitTimeout(
		args.state.wsWaitTimeoutId
	);
	args.state.wsWaitTimeoutId = setTimeout(() => {
		if (!args.isActiveRequest(args.requestToken)) {
			return;
		}
		if (args.state.wsWaitRequestToken !== args.requestToken) {
			return;
		}
		clearPreviewWsWaitState(args.state, args.bumpRevision);
		args.finalizeRequest(args.requestToken);
		args.callbacks?.onWsWaitTimeout?.(CARD_PREVIEW_WS_WAIT_TIMEOUT_MESSAGE);
	}, CARD_PREVIEW_WS_WAIT_TIMEOUT_MS);
}

export type CancelInFlightOptions = {
	/** Keep isRefreshing true across a synchronous cancel→restart handoff. */
	preserveRefreshing?: boolean;
};

export function runPreviewWaitForPersistedDataOrFetch<
	TConfig,
	TCallbacks extends { onWsWaitTimeout?: (message: string) => void },
>(
	opts: {
		cancelInFlight: (options?: CancelInFlightOptions) => void;
		assignRequestToken: () => number;
		setRefreshing: (refreshing: boolean) => void;
		readBaselineFingerprint: (cardId: string) => string;
		beginWsWait: (
			cardId: string,
			requestToken: number,
			baselineFingerprint: string,
			callbacks?: TCallbacks
		) => void;
		fetch: (
			cardId: string,
			timeDimension: Record<string, unknown>,
			cardConfig: TConfig,
			callbacks?: TCallbacks
		) => void;
		isActiveRequest: (requestToken: number) => boolean;
	},
	cardId: string,
	timeDimension: Record<string, unknown>,
	cardConfig: TConfig,
	callbacks?: TCallbacks
): void {
	opts.cancelInFlight({ preserveRefreshing: true });
	const requestToken = opts.assignRequestToken();
	opts.setRefreshing(true);
	const waitBaselineFingerprint = opts.readBaselineFingerprint(cardId);
	opts.beginWsWait(cardId, requestToken, waitBaselineFingerprint, {
		...callbacks,
		onWsWaitTimeout: msg => {
			callbacks?.onWsWaitTimeout?.(msg);
			if (!opts.isActiveRequest(requestToken)) {
				return;
			}
			opts.fetch(cardId, timeDimension, cardConfig, callbacks);
		},
	} as TCallbacks);
}

export function beginPreviewDataLoaderFetchRequest(args: {
	cardId: string;
	activeRequestToken: number | undefined;
	cancelInFlight: () => void;
	assignRequestToken: () => number;
	setRefreshing: (refreshing: boolean) => void;
	readBaselineFingerprint: (cardId: string) => string;
}): { requestToken: number; waitBaselineFingerprint: string } {
	if (args.activeRequestToken !== undefined) {
		args.cancelInFlight();
	}
	const requestToken = args.assignRequestToken();
	args.setRefreshing(true);
	return {
		requestToken,
		waitBaselineFingerprint: args.readBaselineFingerprint(args.cardId),
	};
}
